import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';

/**
 * A list that stands in for a table on a phone (below sm). A table's columns
 * squeeze names to a word or two at 375px; a row here gives the name the
 * full width, one line of detail under it, and one figure on the right.
 *
 * The page keeps its table for sm and up (give its Card max-sm:hidden).
 * Row actions (edit, archive, delete) stay on the table: a phone row opens
 * the record instead.
 *
 *   <PhoneList>
 *     <PhoneListRow title=... detail=... trailing=... onClick=... />
 *   </PhoneList>
 *
 * Used by Equipment, an item's income history, and Clients.
 */
export function PhoneList({ children, className, ...props }) {
  return (
    <ul className={cn('sm:hidden rounded-tile bg-card divide-y divide-line overflow-hidden', className)} {...props}>
      {children}
    </ul>
  );
}

/**
 * One row. Opens `to` (a link) or calls `onClick`; with neither it is plain.
 *   leading  - optional, before the text (a client's monogram)
 *   title    - the record's name, shown in full
 *   detail   - one line under it, muted
 *   trailing - optional, on the right (a figure)
 */
export function PhoneListRow({ to, onClick, leading, title, detail, trailing, muted = false, className, ...props }) {
  const rowClass = cn(
    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
    (to || onClick) && 'hover:bg-mist/50',
    muted && 'opacity-75',
    className
  );
  const body = (
    <>
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="flex-1 min-w-0">
        <span className="block font-medium leading-snug">{title}</span>
        {detail && <span className="mt-1 flex items-center gap-2 text-sm text-muted-foreground min-w-0">{detail}</span>}
      </span>
      {trailing && <span className="shrink-0 tabular-nums">{trailing}</span>}
    </>
  );
  return (
    <li>
      {to ? (
        <Link to={to} className={rowClass} {...props}>{body}</Link>
      ) : onClick ? (
        <button type="button" onClick={onClick} className={rowClass} {...props}>{body}</button>
      ) : (
        <div className={rowClass} {...props}>{body}</div>
      )}
    </li>
  );
}
